import { Typography } from "@/components/common/Typography/Typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  SharepointBrowseItem,
  SharepointSite,
} from "@/types/sharepointConnection"
import { classNames } from "@/utils/classNames"
import { getFileExtension } from "@/utils/fileUtils"
import {
  IconCheck,
  IconChevronRight,
  IconFile,
  IconFolder,
  IconLoader2,
} from "@tabler/icons-react"

interface SharepointFolderBrowserProps {
  selectedSite: SharepointSite | null
  items: SharepointBrowseItem[]
  breadcrumbs: string[]
  loading: boolean
  onFolderClick: (folder: SharepointBrowseItem) => void
  onBreadcrumbClick: (index: number) => void
  selectedItemIds: Set<string>
  onItemToggle: (item: SharepointBrowseItem) => void
  importedItemNames?: Set<string>
  isValidFile?: (name: string) => boolean
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B"
  const k = 1024
  const sizes = ["B", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export const SharepointFolderBrowser: React.FC<
  SharepointFolderBrowserProps
> = ({
  selectedSite,
  items,
  breadcrumbs,
  loading,
  onFolderClick,
  onBreadcrumbClick,
  selectedItemIds,
  onItemToggle,
  importedItemNames = new Set(),
  isValidFile,
}) => {
  const { translate } = useTranslation()
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-grey3 bg-grey2">
      {/* Breadcrumbs */}
      {selectedSite && (
        <div className="flex items-center gap-1 border-b border-grey3 px-3 py-2 text-xs">
          <button
            onClick={() => onBreadcrumbClick(-1)}
            className={classNames(
              "rounded px-1.5 py-0.5 transition-colors",
              breadcrumbs.length === 0
                ? "bg-grey3 text-white"
                : "text-grey5 hover:text-white",
            )}
          >
            {selectedSite.name}
          </button>
          {breadcrumbs.map((crumb, index) => (
            <span key={index} className="flex items-center gap-1">
              <IconChevronRight size={12} className="text-yellow1/50" />
              <button
                onClick={() => onBreadcrumbClick(index)}
                className={classNames(
                  "rounded px-1.5 py-0.5 transition-colors",
                  index === breadcrumbs.length - 1
                    ? "bg-grey3 text-white"
                    : "text-grey5 hover:text-white",
                )}
              >
                {crumb}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <IconLoader2 size={22} className="animate-spin text-yellow1" />
          </div>
        ) : !selectedSite ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <IconFolder size={32} className="mb-2 text-yellow1" />
            <Typography variant="textS" as="p" className="text-grey5">
              {translate("SHAREPOINT/BROWSER_SELECT_SITE_HINT")}
            </Typography>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Typography variant="textS" as="p" className="text-grey5">
              {translate("SHAREPOINT/BROWSER_EMPTY_FOLDER")}
            </Typography>
          </div>
        ) : (
          <div className="flex flex-col">
            {items.map((item) => {
              const isSelected = selectedItemIds.has(item.id)
              const isImported =
                !item.isFolder && importedItemNames.has(item.name)
              const isInvalidFile =
                !item.isFolder && isValidFile && !isValidFile(item.name)
              const ext = !item.isFolder ? getFileExtension(item.name) : ""

              return (
                <div
                  key={item.id}
                  className={classNames(
                    "group flex items-center gap-3 border-b border-grey3/50 px-3 py-2.5 last:border-b-0",
                    isImported || isInvalidFile
                      ? "opacity-40"
                      : isSelected
                        ? "bg-yellow1/5"
                        : "hover:bg-grey3/50",
                  )}
                >
                  {/* Checkbox or imported indicator */}
                  {isImported ? (
                    <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded bg-yellow1/20">
                      <IconCheck size={12} className="text-yellow1" />
                    </div>
                  ) : (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onItemToggle(item)}
                      disabled={isInvalidFile}
                      className="h-4 w-4 shrink-0 rounded border-grey4 accent-yellow1 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  )}

                  {/* Icon */}
                  {item.isFolder ? (
                    <IconFolder size={16} className="text-yellow1" />
                  ) : (
                    <IconFile size={16} className="text-yellow1" />
                  )}

                  {/* Name + details */}
                  {item.isFolder ? (
                    <button
                      onClick={() => onFolderClick(item)}
                      className="flex flex-1 items-center gap-2 text-left"
                    >
                      <span className="text-sm text-white">{item.name}</span>
                      <span className="ml-auto text-xs text-grey5">
                        {item.childCount !== undefined
                          ? translate("SHAREPOINT/BROWSER_ITEM_COUNT", {
                              count: item.childCount,
                            })
                          : ""}
                      </span>
                      <IconChevronRight size={14} className="text-yellow1/50" />
                    </button>
                  ) : (
                    <div className="flex flex-1 items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-white">
                          {item.name}
                        </div>
                        <div className="text-xs text-grey5">
                          {formatFileSize(item.size || 0)}
                          {item.lastModifiedDateTime &&
                            ` · ${formatDate(item.lastModifiedDateTime)}`}
                        </div>
                      </div>
                      {ext && (
                        <span className="shrink-0 rounded bg-yellow1/10 px-1.5 py-0.5 text-xs text-yellow1/70">
                          .{ext}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
